import { Check, Search, UserMinus, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { FadeIn } from '../components/AnimatedPanel';
import type { FriendEntry } from '../types';

export function FriendsPanel() {
  const navigate = useNavigate();
  const [friends, setFriends] = useState<FriendEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; username: string; avatarColor: string }>>([]);
  const [error, setError] = useState('');

  const loadFriends = useCallback(async () => {
    setLoading(true);
    try {
      const { friends: list } = await api.getFriends();
      setFriends(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load friends.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFriends();
  }, [loadFriends]);

  useEffect(() => {
    if (search.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      void api.searchUsers(search).then(({ users }) =>
        setSearchResults(users.map((u) => ({ id: u.id, username: u.username, avatarColor: u.avatarColor }))),
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  async function handleAccept(friendshipId: string) {
    await api.acceptFriendRequest(friendshipId);
    await loadFriends();
  }

  async function handleDecline(friendshipId: string) {
    await api.declineFriendRequest(friendshipId);
    await loadFriends();
  }

  async function handleRemove(username: string) {
    await api.removeFriend(username);
    await loadFriends();
  }

  const accepted = friends.filter((f) => f.status === 'accepted');
  const pendingIn = friends.filter((f) => f.status === 'pending' && f.direction === 'incoming');
  const pendingOut = friends.filter((f) => f.status === 'pending' && f.direction === 'outgoing');

  return (
    <div className="space-y-6">
      <FadeIn>
        <h2 className="font-display text-3xl font-bold">Friends</h2>
        <p className="text-korvex-muted">Connect with other Korvex creators and players.</p>
      </FadeIn>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-korvex-muted" />
        <input
          className="input-field pl-11"
          placeholder="Search players to add…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {searchResults.length > 0 && (
          <div className="absolute z-20 mt-2 w-full rounded-xl border border-white/10 bg-korvex-card p-2 shadow-glow">
            {searchResults.map((u) => (
              <button
                key={u.id}
                type="button"
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/5"
                onClick={() => navigate(`/profile/${u.username}`)}
              >
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: u.avatarColor }}
                >
                  {u.username[0]?.toUpperCase()}
                </span>
                {u.username}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {pendingIn.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-korvex-muted">
            Incoming requests
          </h3>
          <div className="space-y-2">
            {pendingIn.map((f) => (
              <FriendRow
                key={f.id}
                friend={f}
                actions={
                  <>
                    <button type="button" className="btn-ghost px-3 py-1.5" onClick={() => void handleAccept(f.friendshipId)}>
                      <Check className="h-4 w-4 text-green-400" />
                      Accept
                    </button>
                    <button type="button" className="btn-ghost px-3 py-1.5" onClick={() => void handleDecline(f.friendshipId)}>
                      <X className="h-4 w-4" />
                    </button>
                  </>
                }
              />
            ))}
          </div>
        </section>
      )}

      {pendingOut.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-korvex-muted">Sent requests</h3>
          <div className="space-y-2">
            {pendingOut.map((f) => (
              <FriendRow
                key={f.id}
                friend={f}
                badge="Pending"
                actions={
                  <button type="button" className="btn-ghost px-3 py-1.5" onClick={() => void handleDecline(f.friendshipId)}>
                    Cancel
                  </button>
                }
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-korvex-muted">
          Friends ({accepted.length})
        </h3>
        {loading ? (
          <p className="text-korvex-muted">Loading…</p>
        ) : accepted.length === 0 ? (
          <div className="panel-card text-center text-korvex-muted">
            <UserPlus className="mx-auto mb-2 h-8 w-8 opacity-50" />
            No friends yet — visit a profile and send a request.
          </div>
        ) : (
          <div className="space-y-2">
            {accepted.map((f) => (
              <FriendRow
                key={f.id}
                friend={f}
                onOpen={() => navigate(`/profile/${f.username}`)}
                actions={
                  <button type="button" className="btn-ghost px-3 py-1.5" onClick={() => void handleRemove(f.username)}>
                    <UserMinus className="h-4 w-4" />
                    Remove
                  </button>
                }
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function FriendRow({
  friend,
  actions,
  badge,
  onOpen,
}: {
  friend: FriendEntry;
  actions?: React.ReactNode;
  badge?: string;
  onOpen?: () => void;
}) {
  return (
    <div className="panel-card flex items-center gap-4 py-4">
      <button type="button" className="flex flex-1 items-center gap-3 text-left" onClick={onOpen}>
        <span
          className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white"
          style={{ backgroundColor: friend.avatarColor }}
        >
          {friend.username[0]?.toUpperCase()}
        </span>
        <div>
          <p className="font-semibold">{friend.username}</p>
          {badge && <span className="text-xs text-korvex-accent">{badge}</span>}
        </div>
      </button>
      <div className="flex gap-2">{actions}</div>
    </div>
  );
}
